import React from "react";

import { cloneDeep, set } from "lodash";
import { api } from "services";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { FundingProgramContext } from "./FundingProgramContext";


const calcHiddenOptionsCount = response => {
	return response.totalPages > 1 && response.totalElements - response.content.length;
};

/**
 * Use this hof to avoid calling callback in the same render cycle.
 *
 * @param callback
 * @return {function(*): void}
 */
const useCallbackInNexStateUpdate = (callback) => {
	const [ callbackArgs, setCallbackArgs ] = React.useState([]);

	React.useEffect(() => {
		if (isNonEmptyArray(callbackArgs)) {
			callbackArgs.forEach((args) => {
				callback(args);
			});
			setCallbackArgs([]);
		}
	}, [ callbackArgs.length ]);

	return (args) => {
		return setCallbackArgs(prev => [
			...prev,
			args,
		]);
	};
};

export const useFundingProgramContext = ({ programIndex }) => {
	const ctx = React.useContext(FundingProgramContext);

	const current = ctx.programs[programIndex];

	const loadProgramOptions = async inputValue => {
		const response = await api.fundingPrograms.findAll({
			search: inputValue,
		});

		const options = (response.content ?? []).map((item) => ({
			value: item.fundingProgramId,
			label: `${item.organizationName} - ${item.fundingProgramName}`,
			item: {},
		}));

		return {
			options,
			hiddenOptionsCount: calcHiddenOptionsCount(response),
		};
	};

	const loadRoundOptions = async inputValue => {
		const response = await api.fundingProgramRounds.findAll(
			current.program.value,
			{ search: inputValue },
		);

		const options = (response.content ?? []).map(round => ({
			value: round.id,
			label: `${round.name} - ${round.year}`,
			item: {},
		}));

		return {
			options,
			hiddenOptionsCount: calcHiddenOptionsCount(response),
		};
	};

	const updateCurrent = useCallbackInNexStateUpdate(([ key, fullObject, label, hasRounds ]) => {
		const updatedOption = cloneDeep(current);
		set(updatedOption, `${key}.item`, fullObject);
		if (typeof hasRounds === "boolean") {
			set(updatedOption, `${key}.hasRounds`, hasRounds);
		}
		// label is empty when component is mounted first time
		if (!updatedOption.label) {
			set(updatedOption, `${key}.label`, label);
		}

		ctx.onChange(programIndex, updatedOption);
	});

	const loadProgramByIdAndUpdateCurrent = async (id) => {
		const fullProgramObject = await api.fundingPrograms.findById(id);
		const programRounds = await api.fundingProgramRounds.findAll(id, { size: 1 });

		updateCurrent([
			"program",
			fullProgramObject,
			`${fullProgramObject.organizationName} - ${fullProgramObject.name}`,
			programRounds && programRounds.totalElements > 0
		]);
	};

	const loadRoundByIdAndUpdateCurrent = async (id) => {
		const fullRoundObject = await api.fundingProgramRounds.findById(current.program.value, id);

		updateCurrent([
			"round",
			fullRoundObject,
			`${fullRoundObject.name} - ${fullRoundObject.year}`
		]);
	};

	const onSelectProgram = value => {
		if (!value) {
			ctx.onChange(programIndex, { program: { value: "" }, round: null });
		} else if (current.program.value !== value.value) {
			ctx.onChange(programIndex, { program: value, round: null });
		}
	};

	const onRemoveProgram = () => {
		ctx.onChange(programIndex, null);
	};

	const onAddRound = () => {
		ctx.onChange(programIndex, set(cloneDeep(current), "round", {
			value: "",
			label: "",
			item: {},
		}));
	};

	const onSelectRound = value => {
		if (!value) {
			ctx.onChange(programIndex, set(cloneDeep(current), "round", {
				value: "",
				label: "",
				item: {},
			}));
		} else {
			ctx.onChange(programIndex, set(cloneDeep(current), "round", value));
		}
	};

	const onRemoveRound = () => {
		ctx.onChange(programIndex, set(cloneDeep(current), "round", null));
	};

	return {
		...ctx,
		current,
		loadProgramOptions,
		loadRoundOptions,
		loadProgramByIdAndUpdateCurrent,
		loadRoundByIdAndUpdateCurrent,
		onSelectProgram,
		onRemoveProgram,
		onAddRound,
		onSelectRound,
		onRemoveRound,
	};
};
